/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useState, useEffect } from 'react';
import {
  MDXEditor,
  linkPlugin,
  markdownShortcutPlugin,
  linkDialogPlugin,
  listsPlugin,
  headingsPlugin,
} from '@mdxeditor/editor';
import {
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  SidePanelLoader,
  Popup,
  IconPaper,
  ButtonPrimary,
  ButtonSecondary,
} from '@ui/src';
import { useAppDispatch } from '@store';
import { clearTopUpInstructions } from '../hoc/store/slices/message';
import TopUpPanel from './top-up-panel';
import { AddFilesTypes } from './types';

import cn from './messages.module.css';

const AddFiles = ({
  topUpOptions,
  panelIsOpen,
  setPanelIsOpen,
  popupVisible,
  setPopupVisible,
  requestInProgress,
  attachment,
  onDeleteFile,
  onSendMessage,
  fileUploadIsSuccess,
  uploadError,
  setSelectedTopUpId,
  selectedTopUpId,
  topUpIsLoading,
  setTopUpCurrency,
  topUpCurrency,
  currencies,
  baseCurrency,
  topUpAccount,
  setTopUpAccount,
  accounts,
  instructionsIsLoading,
  topUpInstructions,
  getTopUpInstructionPdf,
  pdfDownloadInProgress,
  selectedInstructionType,
  topUpName,
  markdownRef,
  setUploadError,
}: AddFilesTypes) => {
  const [messageValue, setMessageValue] = useState('');
  const [focused, setFocused] = useState(false);

  let content = <SidePanelLoader />;

  const dispatch = useAppDispatch();

  if (!topUpIsLoading) {
    content = (
      <TopUpPanel
        topUpOptions={topUpOptions}
        setSelectedTopUpId={setSelectedTopUpId}
        selectedTopUpId={selectedTopUpId}
        topUpCurrency={topUpCurrency}
        setTopUpCurrency={setTopUpCurrency}
        currencies={currencies}
        baseCurrency={baseCurrency}
        topUpAccount={topUpAccount}
        setTopUpAccount={setTopUpAccount}
        topUpAccounts={accounts?.map((item) => ({
          accountId: item.id,
          account: { fullNumber: item.fullNumber },
          description: item.description,
        }))}
        instructionsIsLoading={instructionsIsLoading}
        topUpInstructions={topUpInstructions}
        getTopUpInstructionPdf={getTopUpInstructionPdf}
        pdfDownloadInProgress={pdfDownloadInProgress}
        selectedInstructionType={selectedInstructionType}
      />
    );
  }

  useEffect(() => {
    if (fileUploadIsSuccess) {
      setMessageValue('');
    }
  }, [fileUploadIsSuccess]);

  useEffect(() => {
    if (popupVisible && markdownRef?.current) {
      markdownRef?.current?.focus();
      setFocused(true);
    }
  }, [popupVisible, markdownRef]);

  return (
    <>
      <Popup
        onCancelClick={() => {
          setPopupVisible(false);
          setMessageValue('');
          onDeleteFile();
          setFocused(false);
          setUploadError('');

          markdownRef?.current?.setMarkdown('');
        }}
        onOkClick={() => {}}
        isVisible={popupVisible}
        parent={document.getElementById('add-file-popup') as HTMLElement}
      >
        <>
          <div className={cn.selectedFilesList}>
            {attachment && (
              <div className={cn.selectedFile}>
                <div className={cn.selectedFileLeft}>
                  <div className={cn.fileIcon}>
                    <IconPaper />
                  </div>
                  <div className={cn.fileName}>
                    {attachment.name || topUpName}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className={cn.popupMarkdownWrapper}
            onKeyUp={(e) => {
              if (e.key === 'Enter' && e.shiftKey) {
                onSendMessage();
                setFocused(false);
              }
            }}
            onMouseUp={(e) => {
              if (e.button === 0 && !focused) {
                setFocused(true);
              }
            }}
          >
            <div className={cn.popupMarkdownTitle}>Message</div>
            <MDXEditor
              ref={markdownRef}
              className={`${cn.popupMarkdown} ${
                requestInProgress ? cn.disabled : ''
              } ${focused ? cn.focused : ''}`}
              markdown={messageValue || ''}
              placeholder="Text here..."
              onChange={(e) => {
                setMessageValue(e);
              }}
              onBlur={() => setFocused(false)}
              plugins={[
                linkPlugin(),
                linkDialogPlugin(),
                listsPlugin(),
                markdownShortcutPlugin(),
                headingsPlugin(),
              ]}
            />
          </div>
          {uploadError && <div className={cn.uploadError}>{uploadError}</div>}
          <div className={cn.addFilesPopupControls}>
            <div className={cn.addFilesPopupControlsRight}>
              <ButtonPrimary
                text={requestInProgress ? 'Loading...' : 'Send'}
                onClick={() => onSendMessage()}
                tabIndex={0}
                bold
              />
              <ButtonPseudo
                text="Cancel"
                onClick={() => {
                  setPopupVisible(false);
                  setMessageValue('');
                  onDeleteFile();
                  setFocused(false);
                  setUploadError('');

                  markdownRef?.current?.setMarkdown('');
                }}
                bold
              />
            </div>
          </div>
        </>
      </Popup>
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={() => {
          setPanelIsOpen(false);
          setSelectedTopUpId('');
          setTopUpCurrency('');
          setTopUpAccount('');
          dispatch(clearTopUpInstructions());
        }}
        title="Download Top-up options"
        sidepanelExpandedKey="donwload-top-up-messages"
        footer={
          <ButtonSecondary
            text="Cancel"
            onClick={() => setPanelIsOpen(false)}
            className="button-white"
            bold
          />
        }
      >
        {content}
      </SidePanel>
    </>
  );
};

export default AddFiles;
